import React, { useMemo, useState } from "react";
import { CustomTooltip } from "../../CustomTooltip";

type AnnualCostItem = {
  label: string;
  amountUsd?: number;
  optional?: boolean;
  tooltip?: string;
};

type AnnualMandatoryCostsProps = {
  title?: string;
  items?: AnnualCostItem[];
  currencySuffix?: string;
  defaultExpanded?: boolean;
  fromYearLabel?: string;
};

const formatUsd = (value: number) => value.toLocaleString("en-US");

const AnnualMandatoryCosts: React.FC<AnnualMandatoryCostsProps> = ({
  title = "Costos anuales obligatorios (desde el segundo año)",
  currencySuffix = "US$",
  defaultExpanded = false,
  fromYearLabel = "A partir del segundo año",
  items = [
    { label: "Agente residente", amountUsd: 200 },
    { label: "Tasa de cumplimiento (abogado)", amountUsd: 50 },
    { label: "Tasa única DGI", amountUsd: 300 },
    {
      label: "Directores provistos por el abogado",
      optional: true,
      tooltip: "Solo se cobrará si eliges la opción de que los directores los provea el abogado. Desde $450 anuales.",
    },
  ],
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const firstItem = items[0];
  const remainingItems = items.slice(1);

  const minTotal = useMemo(
    () =>
      items.reduce(
        (acc, item) => acc + (item.optional ? 0 : item.amountUsd ?? 0),
        0
      ),
    [items]
  );

  const renderLabel = (item: AnnualCostItem) => (
    <span className="text-muted size-12 d-inline-flex align-items-center">
      {item.label}
      {item.tooltip && <CustomTooltip text={item.tooltip} />}
    </span>
  );

  return (
    <div
      className="mt-3 p-3 rounded"
      style={{
        backgroundColor: "#f8f9fa",
        border: "1px solid #e0e0e0",
      }}
    >
      <p className="fw-bold mb-2 size-13 text-black">{title}</p>

      {/* ---------- Collapsed Preview Row ---------- */}
      {!expanded && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            {renderLabel(firstItem)}

            <span className="text-muted size-12">
              {firstItem.optional
                ? "Opcional"
                : `${formatUsd(firstItem.amountUsd ?? 0)} ${currencySuffix}`}
            </span>
          </div>

          <button
            type="button"
            className="btn p-0 mt-1 size-14 text-blue"
            onClick={() => setExpanded(true)}
          >
            Ver más
          </button>
        </>
      )}

      {/* ---------- Expanded Full View ---------- */}
      {expanded && (
        <>
          {/* First item remains visible */}
          <div className="d-flex justify-content-between align-items-center">
            {renderLabel(firstItem)}

            <span className="text-muted size-12">
              {firstItem.optional
                ? "Opcional"
                : `${formatUsd(firstItem.amountUsd ?? 0)} ${currencySuffix}`}
            </span>
          </div>

          {/* Remaining rows */}
          {remainingItems.map((item, idx) => (
            <div
              key={idx}
              className="d-flex justify-content-between align-items-center mt-1"
            >
              {renderLabel(item)}

              <span className="text-muted size-12">
                {item.optional
                  ? "Opcional"
                  : `${formatUsd(item.amountUsd ?? 0)} ${currencySuffix}`}
              </span>
            </div>
          ))}

          <hr className="my-2" />

          {/* TOTAL */}
          <div className="d-flex justify-content-between align-items-start">
            <span className="fw-bold size-14">TOTAL</span>

            <div className="text-end">
              <div className="fw-bold size-14">
                Desde ${formatUsd(minTotal)}
              </div>
              <div className="text-muted size-12">{fromYearLabel}</div>
            </div>
          </div>

          <button
            type="button"
            className="btn p-0 mt-1 size-14 text-blue"
            onClick={() => setExpanded(false)}
          >
            Ver menos
          </button>
        </>
      )}
    </div>
  );
};

export default AnnualMandatoryCosts;
